import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  FertWorkDetailRecord,
  useUpdateSessionDetailMutation,
} from '@/app/services/fertWork';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/services/toast';

export type FixIssuesModalProps = {
  visible: boolean;
  onHide: () => void;
  sessionId: string;
  sessionDetails: FertWorkDetailRecord[];
  issues: Array<FixableIssue>;
};

type FixableIssue = {
  id: string;
  donor: string;
  message: string;
  issueType?: string;
};

type IssueType = 'sire' | 'account' | 'shipLabel' | 'oocytes' | 'matTime';

type IssueGroup = {
  type: IssueType;
  title: string;
  subtitle: string;
  donor: string;
  issues: Array<FixableIssue>;
};

const resolveIssueType = (issue: FixableIssue): IssueType | null => {
  switch (issue.issueType) {
    case 'MissingSire':
      return 'sire';
    case 'MissingAccount':
      return 'account';
    case 'MissingShipLabel':
      return 'shipLabel';
    case 'NoOocytes':
      return 'oocytes';
    case 'InvalidMatTime':
      return 'matTime';
  }

  const lowerMessage = issue.message.toLowerCase();
  if (lowerMessage.includes('missing sire')) return 'sire';
  if (
    lowerMessage.includes('missing customer account') ||
    lowerMessage.includes('missing account') ||
    lowerMessage.includes('ranch id')
  )
    return 'account';
  if (lowerMessage.includes('missing ship label')) return 'shipLabel';
  if (lowerMessage.includes('no oocytes')) return 'oocytes';
  if (lowerMessage.includes('invalid mat time')) return 'matTime';
  return null;
};

const shipLabelOptions = [
  { label: 'Ship', value: 'Ship' },
  { label: 'Ship Freeze', value: 'Ship Freeze' },
  { label: 'Ship Transfer', value: 'Ship Transfer' },
  { label: 'Ship Day 5', value: 'Ship Day 5' },
  { label: 'Freeze Only', value: 'Freeze Only' },
  { label: 'Freeze Only EU', value: 'Freeze Only EU' },
  { label: 'Freeze Only AU', value: 'Freeze Only AU' },
  { label: 'Freeze Only UK', value: 'Freeze Only UK' },
  { label: 'Ship EU', value: 'Ship EU' },
  { label: 'Ship AU', value: 'Ship AU' },
  { label: 'Ship UK', value: 'Ship UK' },
  { label: 'Transfer', value: 'Transfer' },
];

export const FixIssuesModal = ({
  visible,
  onHide,
  sessionId,
  sessionDetails,
  issues,
}: FixIssuesModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [updateSessionDetail, { isLoading }] = useUpdateSessionDetailMutation();

  const [sireNames, setSireNames] = useState<Record<string, string>>({});
  const [sireBreeds, setSireBreeds] = useState<Record<string, string>>({});
  const [accounts, setAccounts] = useState<Record<string, string>>({});
  const [shipLabels, setShipLabels] = useState<Record<string, string>>({});
  const [matTimes, setMatTimes] = useState<Record<string, Date | null>>({});

  const groupIssuesByType = (): IssueGroup[] => {
    const groups: IssueGroup[] = [];
    const processedDonors = new Set<string>();

    issues.forEach((issue) => {
      const type = resolveIssueType(issue);

      if (type && !processedDonors.has(`${issue.donor}-${type}`)) {
        const relatedIssues = issues.filter(
          (i) => i.donor === issue.donor && resolveIssueType(i) === type,
        );

        let title = '';
        let subtitle = '';

        switch (type) {
          case 'sire':
            title = t('fertWork.missingSire', 'Missing Sire');
            subtitle = t(
              'fertWork.enterSireNameAndBreed',
              'Enter Sire Name and Breed',
            );
            break;
          case 'account':
            title = t(
              'fertWork.missingCustomerAccount',
              'Missing Customer Account',
            );
            subtitle = t(
              'fertWork.enterCustomerAccount',
              'Enter Customer Account (Ranch ID)',
            );
            break;
          case 'shipLabel':
            title = t('fertWork.missingShipLabel', 'Missing Ship Label');
            subtitle = t('fertWork.selectAShipLabel', 'Select a Ship Label');
            break;
          case 'oocytes':
            title = t('fertWork.oocytesIssue', 'Oocytes Issue');
            subtitle = t(
              'fertWork.noOocytesInMat',
              'No oocytes detected in mat',
            );
            break;
          case 'matTime':
            title = t('fertWork.matTime', 'Mat Time');
            subtitle = t(
              'fertWork.enterValidMatTime',
              'Enter a valid Mat Time',
            );
            break;
        }

        groups.push({
          type,
          title,
          subtitle,
          donor: issue.donor,
          issues: relatedIssues,
        });

        processedDonors.add(`${issue.donor}-${type}`);
      }
    });

    return groups;
  };

  const issueGroups = groupIssuesByType();

  const handleFixAll = async () => {
    try {
      const donorUpdates = new Map<string, Partial<FertWorkDetailRecord>>();

      issueGroups.forEach((group) => {
        const detailRecord = sessionDetails.find(
          (d) => d.donorName === group.donor,
        );
        if (!detailRecord) return;

        if (!donorUpdates.has(group.donor)) {
          donorUpdates.set(group.donor, {});
        }

        const updates = donorUpdates.get(group.donor)!;

        switch (group.type) {
          case 'sire':
            if (sireNames[group.donor]) {
              updates.sireName = sireNames[group.donor];
              updates.sireId = sireNames[group.donor];
            }
            if (sireBreeds[group.donor]) {
              updates.sireBreed = sireBreeds[group.donor];
            }
            break;
          case 'account':
            if (accounts[group.donor]) {
              updates.accountName = accounts[group.donor];
              updates.accountId = accounts[group.donor];
            }
            break;
          case 'shipLabel':
            if (shipLabels[group.donor]) {
              updates.shipLabel = shipLabels[group.donor];
            }
            break;
          case 'matTime':
            if (matTimes[group.donor]) {
              updates.matTime = matTimes[group.donor].toISOString();
            }
            break;
        }
      });

      const updatePromises = Array.from(donorUpdates.entries()).map(
        ([donorName, updates]) => {
          const detailRecord = sessionDetails.find(
            (d) => d.donorName === donorName,
          );
          if (!detailRecord) return Promise.resolve();

          return updateSessionDetail({
            sessionId,
            detailId: detailRecord.id,
            data: {
              ...detailRecord,
              ...updates,
            },
          }).unwrap();
        },
      );

      await Promise.all(updatePromises);

      setSireNames({});
      setSireBreeds({});
      setAccounts({});
      setShipLabels({});
      setMatTimes({});

      onHide();
    } catch (error) {
      console.error('Failed to fix issues:', error);
      toast.show({
        severity: 'error',
        summary: t('fertWork.toasts.fixIssuesFailed', 'Fix Issues Failed'),
        detail: t(
          'fertWork.toasts.fixIssuesFailedDetail',
          'Failed to fix issues. Please try again.',
        ),
        life: 5000,
      });
    }
  };

  const renderIssueSection = (group: IssueGroup) => {
    switch (group.type) {
      case 'sire':
        return (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-start gap-2">
              <div className="mt-0.5 h-2 w-2 rounded-full bg-red-500" />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{group.title}</div>
                <div className="text-sm text-gray-600">{group.subtitle}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t('fertWork.donor', 'Donor')}
                </label>
                <Input
                  value={group.donor}
                  disabled
                  className="w-full bg-gray-50"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t('fertWork.sireName', 'Sire Name')}
                </label>
                <Input
                  value={sireNames[group.donor] || ''}
                  onChange={(e) =>
                    setSireNames({
                      ...sireNames,
                      [group.donor]: e.target.value,
                    })
                  }
                  placeholder={t(
                    'fertWork.enterSireNamePlaceholder',
                    'Enter sire name (e.g., MESSI 92925)',
                  )}
                  className="w-full"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t('fertWork.sireBreed', 'Sire Breed')}
                </label>
                <Input
                  value={sireBreeds[group.donor] || ''}
                  onChange={(e) =>
                    setSireBreeds({
                      ...sireBreeds,
                      [group.donor]: e.target.value,
                    })
                  }
                  placeholder={t(
                    'fertWork.sireBreedPlaceholder',
                    'HO, AN, JE, etc.',
                  )}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        );

      case 'account':
        return (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-start gap-2">
              <div className="mt-0.5 h-2 w-2 rounded-full bg-red-500" />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{group.title}</div>
                <div className="text-sm text-gray-600">{group.subtitle}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t('fertWork.donor', 'Donor')}
                </label>
                <Input
                  value={group.donor}
                  disabled
                  className="w-full bg-gray-50"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t(
                    'fertWork.customerAccountRanchId',
                    'Customer Account (Ranch ID)',
                  )}
                </label>
                <Input
                  value={accounts[group.donor] || ''}
                  onChange={(e) =>
                    setAccounts({ ...accounts, [group.donor]: e.target.value })
                  }
                  placeholder={t(
                    'fertWork.enterAccountPlaceholder',
                    'Enter account name (e.g., FABRIC, RANCH A)',
                  )}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        );

      case 'shipLabel':
        return (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-start gap-2">
              <div className="mt-0.5 h-2 w-2 rounded-full bg-red-500" />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{group.title}</div>
                <div className="text-sm text-gray-600">{group.subtitle}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t('fertWork.donor', 'Donor')}
                </label>
                <Input
                  value={group.donor}
                  disabled
                  className="w-full bg-gray-50"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t('fertWork.shipLabel', 'Ship Label')}
                </label>
                <Select
                  items={shipLabelOptions}
                  value={shipLabels[group.donor] || null}
                  onChange={(value) =>
                    setShipLabels({
                      ...shipLabels,
                      [group.donor]: value as string,
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue
                      placeholder={t(
                        'fertWork.selectShipLabelPlaceholder',
                        'Select ship label',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {shipLabelOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 'oocytes':
        return (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-start gap-2">
              <div className="mt-0.5 h-2 w-2 rounded-full bg-red-500" />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{group.title}</div>
                <div className="text-sm text-gray-600">{group.subtitle}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t('fertWork.donor', 'Donor')}
                </label>
                <Input
                  value={group.donor}
                  disabled
                  className="w-full bg-gray-50"
                />
              </div>

              <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3">
                <div className="flex items-start gap-2">
                  <i className="pi pi-exclamation-triangle text-yellow-600" />
                  <div className="text-sm text-yellow-900">
                    {t(
                      'fertWork.zeroOocytesWarning',
                      'This mating has 0 oocytes. You may need to delete this row or update the oocyte count in the source data.',
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'matTime':
        return (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-start gap-2">
              <div className="mt-0.5 h-2 w-2 rounded-full bg-red-500" />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{group.title}</div>
                <div className="text-sm text-gray-600">{group.subtitle}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t('fertWork.donor', 'Donor')}
                </label>
                <Input
                  value={group.donor}
                  disabled
                  className="w-full bg-gray-50"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  {t('fertWork.matTime', 'Mat Time')}
                </label>
                <Calendar
                  value={matTimes[group.donor] || null}
                  onChange={(e) =>
                    setMatTimes({ ...matTimes, [group.donor]: e.value as Date })
                  }
                  timeOnly
                  placeholder={t('fertWork.matTimePlaceholder', '--:-- --')}
                  className="w-full"
                  showIcon
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
    >
      <DialogContent className="md:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t('fertWork.fixMultipleIssues', 'Fix Multiple Issues')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {issueGroups.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              {t(
                'fertWork.noFixableIssuesSelected',
                'No fixable issues selected.',
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {issueGroups.map((group, index) => (
                <div key={`${group.donor}-${group.type}-${index}`}>
                  {renderIssueSection(group)}
                </div>
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onHide} disabled={isLoading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="info" onClick={handleFixAll} disabled={isLoading}>
            {isLoading && <i className="pi pi-spin pi-spinner" />}
            {isLoading
              ? t('fertWork.fixing', 'Fixing...')
              : t('fertWork.fixAllIssues', 'Fix All Issues ({count})', {
                  count: issues.length,
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
